import { frameSnapLines, type BoardElement, type Frame, type Rect } from '@praesto/shared'
import type { ResizeCorner } from './ElementView.js'

/**
 * Einrasten beim Verschieben und beim Größenändern.
 *
 * Zwei Arten von Zielen, und beide werden gebraucht:
 *
 * - **Objekte** — Kanten und Mitten der übrigen Elemente und Frames. Damit
 *   richtet man Dinge aneinander aus.
 * - **Der umgebende Frame** — Kanten, Mitte, Satzspiegel und Raster. Damit
 *   richtet man Dinge *auf der Seite* aus, und zwar über alle Seiten hinweg
 *   gleich. Das ist der Unterschied zwischen „sieht ordentlich aus" und „hat
 *   überall denselben Rand".
 *
 * Gesammelt wird einmal beim Anfassen: Die Kanten der übrigen Objekte ändern
 * sich während des Ziehens ohnehin nicht.
 */

/** Fangweite in Bildschirmpixeln — unabhängig vom Zoom gleich „griffig". */
export const SNAP_PX = 6

export interface SnapTargets {
  x: number[]
  y: number[]
  /** Der Frame, in dem gearbeitet wird; für die eingeblendeten Rasterlinien. */
  frame: Frame | null
}

/** Linke Kante, Mitte und rechte Kante eines Rechtecks in einer Achse. */
export const edgesX = (r: Rect): number[] => [r.x, r.x + r.w / 2, r.x + r.w]
export const edgesY = (r: Rect): number[] => [r.y, r.y + r.h / 2, r.y + r.h]

/**
 * Der Frame, in dem ein Objekt liegt: der **kleinste**, der seinen Mittelpunkt
 * enthält.
 *
 * Zuordnung über Geometrie statt über ein Feld im Datenmodell — dieselbe Regel,
 * nach der beim Verschieben eines Frames sein Inhalt mitwandert. Der kleinste
 * gewinnt, weil bei verschachtelten Frames der innere die Bühne ist.
 */
export function frameAround(
  box: Rect,
  frames: readonly Frame[],
  /** Was gerade selbst bewegt wird, kann nicht die Bühne sein. */
  exclude: ReadonlySet<string> = new Set(),
): Frame | null {
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  let best: Frame | null = null
  for (const frame of frames) {
    if (exclude.has(frame.id)) continue
    if (cx < frame.x || cx > frame.x + frame.w || cy < frame.y || cy > frame.y + frame.h) continue
    if (!best || frame.w * frame.h < best.w * best.h) best = frame
  }
  return best
}

export function collectTargets({
  box,
  elements,
  frames,
  moving,
}: {
  box: Rect
  elements: readonly BoardElement[]
  frames: readonly Frame[]
  /** IDs, die selbst bewegt werden und deshalb kein Ziel sein dürfen. */
  moving: ReadonlySet<string>
}): SnapTargets {
  const x: number[] = []
  const y: number[] = []

  for (const element of elements) {
    if (moving.has(element.id)) continue
    x.push(...edgesX(element))
    y.push(...edgesY(element))
  }
  for (const frame of frames) {
    if (moving.has(frame.id)) continue
    x.push(...edgesX(frame))
    y.push(...edgesY(frame))
  }

  // Der umgebende Frame kommt mit seinen inneren Linien dazu. Seine Außenkanten
  // stehen dann doppelt in der Liste — das stört nicht, `findSnap` nimmt ohnehin
  // die nächstgelegene.
  const frame = frameAround(box, frames, moving)
  if (frame) {
    const lines = frameSnapLines(frame)
    x.push(...lines.x)
    y.push(...lines.y)
  }

  return { x, y, frame }
}

export interface Snap {
  delta: number
  line: number
}

/**
 * Nächstgelegener Fangpunkt.
 *
 * Verglichen werden alle drei Kanten des bewegten Objekts gegen alle Kanten der
 * übrigen — so rastet nicht nur „links an links" ein, sondern auch „Mitte an
 * Mitte" und „rechts an links", was beim Anordnen den Unterschied macht.
 */
export function findSnap(moving: number[], targets: number[], tolerance: number): Snap | null {
  let best: Snap | null = null
  for (const own of moving) {
    for (const target of targets) {
      const delta = target - own
      if (Math.abs(delta) > tolerance) continue
      if (!best || Math.abs(delta) < Math.abs(best.delta)) best = { delta, line: target }
    }
  }
  return best
}

export interface ResizeResult {
  rect: Rect
  guides: { v: number[]; h: number[] }
}

/**
 * Größenändern mit Einrasten.
 *
 * Bewegt wird genau eine Ecke; die gegenüberliegende (`anchor`) steht fest. Bei
 * gebundenem Seitenverhältnis darf nur **eine** Achse einrasten — die mit der
 * kleineren Korrektur —, die andere folgt aus dem Verhältnis. Ließe man beide
 * einrasten, zöge das Fangen das Bild aus der Form, und genau das soll die
 * Bindung ja verhindern.
 */
export function snapResize({
  pointer,
  anchor,
  aspect,
  targets,
  tolerance,
  minSize,
}: {
  pointer: { x: number; y: number }
  anchor: { x: number; y: number }
  aspect: number | null
  targets: SnapTargets
  tolerance: number
  minSize: number
}): ResizeResult {
  let cornerX = pointer.x
  let cornerY = pointer.y

  const snapX = findSnap([cornerX], targets.x, tolerance)
  const snapY = findSnap([cornerY], targets.y, tolerance)
  const guides = { v: [] as number[], h: [] as number[] }

  if (aspect === null) {
    if (snapX) {
      cornerX = snapX.line
      guides.v.push(snapX.line)
    }
    if (snapY) {
      cornerY = snapY.line
      guides.h.push(snapY.line)
    }
  } else if (snapX || snapY) {
    const useX = snapY === null || (snapX !== null && Math.abs(snapX.delta) <= Math.abs(snapY.delta))
    if (useX && snapX) {
      cornerX = snapX.line
      guides.v.push(snapX.line)
      const height = Math.abs(cornerX - anchor.x) / aspect
      cornerY = cornerY < anchor.y ? anchor.y - height : anchor.y + height
    } else if (snapY) {
      cornerY = snapY.line
      guides.h.push(snapY.line)
      const width = Math.abs(cornerY - anchor.y) * aspect
      cornerX = cornerX < anchor.x ? anchor.x - width : anchor.x + width
    }
  }

  let w = Math.abs(cornerX - anchor.x)
  let h = Math.abs(cornerY - anchor.y)
  if (aspect !== null) {
    // Die dominante Achse führt, damit das Objekt dem Zeiger folgt, statt bei
    // gemischter Bewegung zu springen.
    if (w / aspect > h) h = w / aspect
    else w = h * aspect
  }
  // Unter der Mindestgröße stimmt die gefangene Kante nicht mehr — dann ist die
  // Hilfslinie eine Lüge und verschwindet.
  if (w < minSize || h < minSize) {
    w = Math.max(minSize, w)
    h = Math.max(minSize, h)
    guides.v.length = 0
    guides.h.length = 0
  }

  return {
    rect: {
      x: cornerX < anchor.x ? anchor.x - w : anchor.x,
      y: cornerY < anchor.y ? anchor.y - h : anchor.y,
      w,
      h,
    },
    guides,
  }
}

/** Die feststehende Ecke beim Ziehen an `corner`. */
/**
 * Der Punkt, der beim Ziehen stehen bleibt — die gegenüberliegende Ecke.
 *
 * Bei einer Kante zählt nur ihre eigene Achse; auf der anderen bleibt der
 * Rahmen ohnehin unverändert, und der Anker dort ist gleichgültig.
 */
export const anchorFor = (box: Rect, corner: ResizeCorner): { x: number; y: number } => ({
  x: corner === 'nw' || corner === 'sw' || corner === 'w' ? box.x + box.w : box.x,
  y: corner === 'nw' || corner === 'ne' || corner === 'n' ? box.y + box.h : box.y,
})

/** Welche Achsen ein Anfasser verändert. */
export const axisOf = (corner: ResizeCorner): { x: boolean; y: boolean } => ({
  x: corner !== 'n' && corner !== 's',
  y: corner !== 'e' && corner !== 'w',
})
